'use client'

import { Button, ButtonProps, styled } from '@mui/joy'
import { theme } from '@ui/style'
import { pxToRem } from '@ui/style/muiTheme'
import { forwardRef } from 'react'

export interface PillButtonProps extends Omit<ButtonProps, 'size' | 'color' | 'variant'> {
	size?: 'sm' | 'md' | 'lg'
	btncolor?: 'neutral' | 'light' | 'dark' | 'live'
}

const PillButtonRoot = styled(Button)<PillButtonProps>(
	({ size = 'md', btncolor = 'light' }: PillButtonProps) => {
		const colorMap = {
			neutral: {
				border: {
					default: theme.color.colGray3,
					pressed: theme.color.colGray1,
				},
				text: {
					default: theme.color.colBlack,
					pressed: theme.color.colBlack,
				},
				background: {
					default: 'transparent',
					pressed: theme.color.colGray1,
				},
			},
			light: {
				border: {
					default: theme.color.colGray3,
					pressed: theme.color.colGray1,
				},
				text: {
					default: theme.color.colGray5,
					pressed: theme.color.colGray5,
				},
				background: {
					default: 'transparent',
					pressed: theme.color.colGray1,
				},
			},
			dark: {
				border: {
					default: theme.color.colGray3,
					pressed: 'transparent',
				},
				text: {
					default: theme.color.colWhite,
					pressed: theme.color.colWhite,
				},
				background: {
					default: 'transparent',
					pressed: 'rgba(255, 255, 255, 0.15)',
				},
			},
			live: {
				border: {
					default: theme.color.colLiveRed,
					pressed: theme.color.colLiveRed,
				},
				text: {
					default: theme.color.colLiveRed,
					pressed: theme.color.colLiveRed,
				},
				background: {
					default: 'transparent',
					pressed: 'transparent',
				},
			},
		}

		const paddingValue = {
			sm: `${pxToRem(4)} ${pxToRem(12)}`,
			md: `${pxToRem(6)} ${pxToRem(12)}`,
			lg: `${pxToRem(8)} ${pxToRem(16)}`,
		}
		const fontSizeValue = {
			sm: `${pxToRem(13)}`,
			md: `${pxToRem(14)}`,
			lg: `${pxToRem(16)}`,
		}

		const styles = {
			fontSize: fontSizeValue[size],
			fontWeight: 600,
			lineHeight: pxToRem(20),
			borderRadius: '9999px',
			minHeight: 0,
			padding: paddingValue[size],
			color: colorMap[btncolor].text.default,
			background: colorMap[btncolor].background.default,
			border: `1px solid ${colorMap[btncolor].border.default}`,
			'&:active, &[aria-expanded="true"]': {
				background: colorMap[btncolor].background.pressed,
				borderColor: colorMap[btncolor].border.pressed,
				color: colorMap[btncolor].text.pressed,
			},
			'&:hover, &:active': {
				background: colorMap[btncolor].background.pressed,
				borderColor: colorMap[btncolor].border.pressed,
				color: colorMap[btncolor].text.pressed,
			},
		}

		return styles
	},
)

const PillButton = forwardRef<HTMLButtonElement, PillButtonProps>(
	({ size, children, btncolor, ...props }, ref) => {
		return (
			<PillButtonRoot ref={ref} size={size} btncolor={btncolor} {...props}>
				{children}
			</PillButtonRoot>
		)
	},
)

export default PillButton
